import type { FC } from 'react';
import React from 'react';

import { cn } from '@wwe-portal/ui/lib/lib';

import { ClickableDiv } from '../button';

import { DialogContent } from './primitive';
import { CloseIcon } from '../icons/close-icon';

export interface PaymentDialogLayoutProps {
  imageElement: React.ReactNode;
  titleElement: React.ReactNode;
  descriptionElement: React.ReactNode;
  actionElement?: React.ReactNode;
  hasBackground?: boolean;
  closable: boolean;
  contentClass?: string;
  layoutClass?: string;
  onClose?: () => void;
  dataId: string;
}

export function DialogCloseButton({
  onClose,
  backgroundColor = '#6A73B5',
  noBackground,
  className,
}: Readonly<{
  onClose?: (e: React.SyntheticEvent) => void;
  backgroundColor?: string;
  className?: string;
  noBackground?: boolean;
}>) {
  const handleClose = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onClose?.(e);
  };
  if (noBackground)
    return (
      <div
        className={cn(`absolute right-0 top-0 -translate-y-[35%]`, className)}
      >
        <ClickableDiv
          className="text-text600 flex cursor-pointer select-none items-center justify-center hover:brightness-125"
          onClick={handleClose}
        >
          <CloseIcon />
        </ClickableDiv>
      </div>
    );

  return (
    <div
      className={cn(
        `absolute right-0 top-0 -translate-y-[35%] translate-x-[35%]`,
        className
      )}
    >
      <ClickableDiv
        className="flex cursor-pointer select-none items-center justify-center hover:brightness-125"
        onClick={handleClose}
      >
        <svg
          className="rounded-full"
          fill="none"
          height="36"
          viewBox="0 0 36 36"
          width="36"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="18" cy="18" fill={backgroundColor} r="18" />
          <path
            d="M14 14L22 22"
            stroke="#E2E4F5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <path
            d="M22 14L14 22"
            stroke="#E2E4F5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      </ClickableDiv>
    </div>
  );
}

export const CommonDialogContentLayout: FC<
  Omit<PaymentDialogLayoutProps, 'contentClass' | 'dataId'>
> = ({
  imageElement,
  titleElement,
  descriptionElement,
  actionElement,
  closable,
  hasBackground,
  onClose,
  layoutClass,
}) => {
  return (
    <div
      className={cn(
        'px-13 py-13 font-poppins text-text400 w-full overflow-visible rounded-lg 2xl:px-24',
        {
          "bg-[url('/assets/images/club-coin-bg.webp')] bg-cover":
            hasBackground,
        },
        layoutClass
      )}
    >
      <div className="flex min-h-full flex-col items-center justify-center gap-5 text-center md:gap-5">
        {imageElement ? (
          <div className="flex items-center justify-center">
            <div className="h-20 w-20">{imageElement}</div>
          </div>
        ) : null}

        {titleElement ? (
          <div className="text-text300 text-xl font-semibold">
            {titleElement}
          </div>
        ) : null}
        {descriptionElement ? (
          <div className="text-text400 -mt-4 text-sm">{descriptionElement}</div>
        ) : null}
        {actionElement}
        {closable ? <DialogCloseButton onClose={onClose} /> : null}
      </div>
    </div>
  );
};

export function CommonDialogLayout({
  contentClass,
  dataId,
  ...rest
}: Readonly<PaymentDialogLayoutProps>) {
  return (
    <DialogContent className={contentClass} data-id={dataId} variant="message">
      <CommonDialogContentLayout {...rest} />
    </DialogContent>
  );
}
